import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import IssueForm from '@/components/IssueForm';
import { storageService } from '@/lib/storage';
import { authService } from '@/lib/auth';
import { toast } from 'sonner';
import { Issue } from '@/types';
import { MapPin, ShieldCheck, Camera } from 'lucide-react';

export default function ReportIssue() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();

  // 🔐 Safety check
  if (!currentUser) {
    navigate('/login');
    return null;
  }

  const handleSubmit = async (
    issueData: Omit<Issue, 'id' | 'reportedAt' | 'updatedAt'>
  ) => {
    setIsSubmitting(true);

    try {
      // ⏳ Simulate API delay (demo realism)
      await new Promise((resolve) => setTimeout(resolve, 1000));

      storageService.saveIssue({
        ...issueData,
        reportedBy: issueData.reportedBy || currentUser.name,
      });

      toast.success('Issue reported successfully 🚀');

      // 🔁 Redirect by role
      navigate(currentUser.role === 'admin' ? '/admin' : '/');
    } catch {
      toast.error('Failed to submit issue. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout title="Report New Issue">
      <div className="max-w-4xl mx-auto space-y-8">

        {/* Header */}
        <div className="text-center space-y-3">
          <h2 className="text-3xl font-bold text-gray-900">
            Report a Street Issue
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Help improve your community by reporting street-level problems.
            Your report goes directly into the city monitoring system.
          </p>
        </div>

        {/* Form */}
        <IssueForm onSubmit={handleSubmit} isSubmitting={isSubmitting} />

        {/* Guidelines */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-4">
          <h3 className="font-semibold text-blue-900 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Reporting Guidelines
          </h3>

          <ul className="space-y-3 text-blue-800 text-sm">
            <li className="flex items-start gap-2">
              <Camera className="h-4 w-4 mt-0.5" />
              Take clear photos showing the issue from multiple angles
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5" />
              Provide accurate location information for faster action
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 mt-0.5" />
              Choose priority based on real safety impact
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 mt-0.5" />
              You can track progress in your dashboard after submission
            </li>
          </ul>
        </div>
      </div>
    </Layout>
  );
}
